import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Upload, Trash2, Loader2, AudioLines, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

const MAX_SECONDS = 30;

/**
 * "Il tuo Avatar Audio" (Phase 71) — profile edit section.
 * Upload mp3/wav OR record from the browser (auto-stop at 30s),
 * replay/delete, plus the example presentation script.
 * Optional for everyone, MANDATORY for VIP Creators.
 */
export const AudioAvatarSection = () => {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [pendingUrl, setPendingUrl] = useState('');

  const isVip = !!user?.is_verified_creator;
  const displayName = user?.full_name || user?.username || '[Nome]';

  useEffect(() => () => {
    clearInterval(timerRef.current);
    try { recorderRef.current?.stop(); } catch (_e) { /* noop */ }
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
  }, [pendingUrl]);

  const uploadBlob = async (blob, filename) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', blob, filename);
      await api.post('/auth/me/audio-avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
      setPendingBlob(null);
      if (pendingUrl) { URL.revokeObjectURL(pendingUrl); setPendingUrl(''); }
      toast({ title: 'Avatar Audio salvato' });
    } catch (e) {
      toast({
        title: 'Salvataggio non riuscito',
        description: String(e?.response?.data?.detail || '').split('|').pop(),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    // Client-side duration guard before hitting the server.
    const url = URL.createObjectURL(file);
    const probe = new Audio(url);
    probe.onloadedmetadata = async () => {
      URL.revokeObjectURL(url);
      if (probe.duration && probe.duration > MAX_SECONDS + 1) {
        toast({
          title: 'Audio troppo lungo',
          description: `Massimo ${MAX_SECONDS} secondi (il tuo dura ${Math.round(probe.duration)}s).`,
          variant: 'destructive',
        });
        return;
      }
      await uploadBlob(file, file.name);
    };
    probe.onerror = async () => {
      URL.revokeObjectURL(url);
      await uploadBlob(file, file.name);
    };
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 0) {
          setPendingBlob(blob);
          setPendingUrl(URL.createObjectURL(blob));
        }
      };
      rec.start();
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s + 1 >= MAX_SECONDS) {
            try { rec.stop(); } catch (_e) { /* noop */ }
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch (_e) {
      toast({
        title: 'Microfono non disponibile',
        description: 'Consenti l\'accesso al microfono dal browser per registrare.',
        variant: 'destructive',
      });
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch (_e) { /* noop */ }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete('/auth/me/audio-avatar');
      await refresh();
      toast({ title: 'Avatar Audio rimosso' });
    } catch (_e) {
      toast({ title: 'Rimozione non riuscita', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="audio-avatar-section" className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4" data-testid="audio-avatar-section">
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <AudioLines className="h-4 w-4 gold-text" /> Il tuo Avatar Audio
        </p>
        {isVip ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider gold-text" data-testid="audio-avatar-required-badge">
            <Crown className="h-3 w-3" /> Obbligatorio VIP
          </span>
        ) : (
          <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Opzionale
          </span>
        )}
      </div>
      <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
        Una breve presentazione vocale (max {MAX_SECONDS} secondi) che gli utenti potranno
        ascoltare dal tuo avatar nel feed e sul profilo.
        {isVip && ' Come Voce Pro è obbligatoria per pubblicare nel feed.'}
      </p>

      {/* Current audio */}
      {user?.audio_avatar_url && !pendingBlob && (
        <div className="mb-3 flex items-center gap-2" data-testid="audio-avatar-current">
          <audio controls src={user.audio_avatar_url} className="h-9 min-w-0 flex-1" preload="none" data-testid="audio-avatar-player" />
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={handleDelete}
            className="h-9 shrink-0 text-red-400 hover:text-red-300"
            data-testid="audio-avatar-delete-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Pending recording preview */}
      {pendingBlob && (
        <div className="mb-3 rounded-lg border border-amber-500/30 bg-background/50 p-2.5" data-testid="audio-avatar-pending">
          <p className="mb-1.5 text-[11px] font-semibold gold-text">Anteprima registrazione</p>
          <audio controls src={pendingUrl} className="mb-2 h-9 w-full" data-testid="audio-avatar-pending-player" />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() => uploadBlob(pendingBlob, `registrazione.${(pendingBlob.type || '').includes('mp4') ? 'm4a' : 'webm'}`)}
              className="gold-gradient h-8 flex-1 text-xs font-bold text-black"
              data-testid="audio-avatar-save-recording-btn"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Usa questa registrazione'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => { setPendingBlob(null); URL.revokeObjectURL(pendingUrl); setPendingUrl(''); }}
              className="h-8 text-xs"
              data-testid="audio-avatar-discard-recording-btn"
            >
              Scarta
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav"
          className="hidden"
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
          data-testid="audio-avatar-file-input"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={busy || recording}
          onClick={() => fileRef.current?.click()}
          className="h-8 text-xs"
          data-testid="audio-avatar-upload-btn"
        >
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Carica file (mp3/wav)
        </Button>
        {recording ? (
          <Button
            size="sm"
            onClick={stopRecording}
            className="h-8 bg-red-600 text-xs font-bold text-white hover:bg-red-500"
            data-testid="audio-avatar-stop-btn"
          >
            <Square className="mr-1.5 h-3.5 w-3.5" /> Stop ({MAX_SECONDS - elapsed}s)
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={startRecording}
            className="h-8 text-xs"
            data-testid="audio-avatar-record-btn"
          >
            <Mic className="mr-1.5 h-3.5 w-3.5" /> Registra dal browser
          </Button>
        )}
      </div>
      {recording && (
        <div className="mt-2 flex items-center gap-2" data-testid="audio-avatar-recording-indicator">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span className="text-[11px] font-semibold text-red-400">
            Registrazione in corso… {elapsed}s / {MAX_SECONDS}s (stop automatico)
          </span>
        </div>
      )}

      {/* Example script */}
      <div className="mt-3 rounded-lg border border-white/10 bg-background/40 p-3" data-testid="audio-avatar-example-box">
        <p className="mb-1 text-[11px] font-bold uppercase tracking-wider gold-text">Esempio di presentazione</p>
        <p className="text-[12px] italic leading-relaxed text-muted-foreground">
          “Ciao! Sono {displayName}. Se hai bisogno di parlare, confrontarti
          o semplicemente di essere ascoltato, sono qui per ascoltarti.”
        </p>
      </div>
    </div>
  );
};

export default AudioAvatarSection;
